import React from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { Button, Toggle, WarningMessage } from '../../../components/widgets';
import { IconVariants } from '../../../components/foundations/Icon';
import FormField from '../FormField';
import FormInnerSection from '../FormInnerSection';
import DeleteIcon from '../DeleteIcon';
import Input from '../Input';
import { ATTRIBUTION_TAB_IDS, EVENT_MAPPING_EVENT_TYPES, TRANSLATE_PREFIX, WHITESPACE_REGEX } from '../../consts';
import useConfirmDialog from '../../useConfirmDialog';
import useStableRowKeys from '../../useStableRowKeys';
import { useTabUpdater } from '../../formUpdaters';
import eventMappingScreenshot from '../../../resources/png/partnerIntegration/event_mapping.png';
import css from './EventMappingSection.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;

const EventMappingSection = ({ translate, readOnly = false }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(TAB_ID);
    const tabData = values.formData?.[TAB_ID] || {};

    const eventsAndPlatforms = tabData.postback_events_and_platforms || [];
    const hasEventMappingType = eventsAndPlatforms.some(
        e => EVENT_MAPPING_EVENT_TYPES.includes(e.event_type) && (e.platforms || []).length > 0
    );

    const enableEventMapping = !!tabData.enable_event_mapping;
    const parameterKeyName = tabData.event_parameter_key_name || '';
    const eventNames = tabData.event_names || [];

    const { keysRef: rowKeysRef, makeKey: makeRowKey } = useStableRowKeys(eventNames.length, 'row');

    const setField = (field, value) => updateTab({ [field]: value });

    const { confirm, dialog } = useConfirmDialog();

    const hasEventMappingData = () => !!parameterKeyName || (tabData.event_names || []).some(n => !!n);

    const enableEventMappingOn = () => {
        const seedNames = !eventNames.length;
        if (seedNames) {
            rowKeysRef.current = [makeRowKey()];
        }
        updateTab({
            enable_event_mapping: true,
            ...(seedNames ? { event_names: [''] } : {}),
        });
    };
    const disableEventMapping = () => {
        rowKeysRef.current = [];
        updateTab({
            enable_event_mapping: false,
            event_parameter_key_name: undefined,
            event_names: undefined,
        });
    };

    const handleToggle = e => {
        const { checked } = e.target;
        if (checked) {
            enableEventMappingOn();
            return;
        }
        if (hasEventMappingData()) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_EVENT_MAPPING_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.DISABLE_EVENT_MAPPING_TEXT`,
                onConfirm: disableEventMapping,
            });
            return;
        }
        disableEventMapping();
    };

    // Compare on trimmed values, including empty strings, so two empty rows count as duplicates
    // (and block draft via the matching ``event_names_duplicate`` Formik error).
    const trimmedKey = name => (typeof name === 'string' ? name.trim() : '');
    const nameCounts = eventNames.reduce((acc, n) => {
        const k = trimmedKey(n);
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});
    const isDuplicate = name => nameCounts[trimmedKey(name)] > 1;

    const updateEventName = (index, value) => {
        updateTab(prevTab => {
            const prevNames = prevTab.event_names || [];
            const next = prevNames.slice();
            next[index] = value;
            return { ...prevTab, event_names: next };
        });
    };
    const addEventName = () => {
        rowKeysRef.current = [...rowKeysRef.current, makeRowKey()];
        updateTab(prevTab => ({
            ...prevTab,
            event_names: [...(prevTab.event_names || []), ''],
        }));
    };
    const removeEventName = index => {
        rowKeysRef.current = rowKeysRef.current.filter((_, i) => i !== index);
        updateTab(prevTab => ({
            ...prevTab,
            event_names: (prevTab.event_names || []).filter((_, i) => i !== index),
        }));
    };

    const duplicateHint = translate(`${TAB_PREFIX}.EVENT_NAME_DUPLICATE_HINT`);
    const noSpacesHint = translate(`${TAB_PREFIX}.NO_SPACES_HINT`);
    const hasSpaces = value => typeof value === 'string' && WHITESPACE_REGEX.test(value);
    const getEventNameError = name => {
        if (hasSpaces(name)) return noSpacesHint;
        if (isDuplicate(name)) return duplicateHint;
        return undefined;
    };

    return (
        <>
            <FormInnerSection
                titleKey={`${TAB_PREFIX}.ENABLE_EVENT_MAPPING_LABEL`}
                descriptionKey={`${TAB_PREFIX}.ENABLE_EVENT_MAPPING_SUBTITLE`}
                anchor="event_mapping"
                disabledMessageKey={
                    !readOnly && !hasEventMappingType
                        ? `${TAB_PREFIX}.ENABLE_EVENT_MAPPING_DISABLED_MESSAGE`
                        : undefined
                }
                headerRight={
                    <Toggle
                        checked={enableEventMapping}
                        onToggle={handleToggle}
                        disabled={readOnly || !hasEventMappingType}
                    />
                }
            >
                {enableEventMapping && (
                    <>
                        <WarningMessage
                            type="info"
                            showIcon={false}
                            collapsible
                            collapsibleTitle={`${TAB_PREFIX}.EVENT_MAPPING_INFO_TITLE`}
                            message={
                                <div className={css.eventMappingInfoContent}>
                                    <Translate id={`${TAB_PREFIX}.EVENT_MAPPING_INFO_CONTENT`} />
                                    <img
                                        src={eventMappingScreenshot}
                                        alt={translate(`${TAB_PREFIX}.EVENT_MAPPING_INFO_TITLE`)}
                                        className={css.eventMappingScreenshot}
                                    />
                                </div>
                            }
                        />
                        <FormField
                            labelKey={`${TAB_PREFIX}.EVENT_PARAMETER_KEY_LABEL`}
                            required={!readOnly}
                            fullWidth
                            className={css.flatField}
                        >
                            <Input
                                value={parameterKeyName}
                                placeholder={translate(`${TAB_PREFIX}.EVENT_PARAMETER_KEY_PLACEHOLDER`)}
                                onChange={val => setField('event_parameter_key_name', val)}
                                disabled={readOnly}
                                error={hasSpaces(parameterKeyName) ? noSpacesHint : undefined}
                            />
                        </FormField>
                        <FormField
                            labelKey={`${TAB_PREFIX}.EVENT_NAMES_LABEL`}
                            subtitleKey={`${TAB_PREFIX}.EVENT_NAMES_SUBTITLE`}
                            required={!readOnly}
                            fullWidth
                            className={css.flatField}
                        >
                            <div className={css.eventNamesList}>
                                {eventNames.map((name, i) => (
                                    <div key={rowKeysRef.current[i]} className={css.eventNameRow}>
                                        <Input
                                            value={name}
                                            placeholder={translate(`${TAB_PREFIX}.EVENT_NAMES_PLACEHOLDER`)}
                                            onChange={val => updateEventName(i, val)}
                                            disabled={readOnly}
                                            error={getEventNameError(name)}
                                            containerClass={css.eventNameInput}
                                            required={!readOnly}
                                        />
                                        {!readOnly && (
                                            <DeleteIcon
                                                icon="trash"
                                                className={css.removeButton}
                                                confirmTitleKey={`${TAB_PREFIX}.CONFIRM_DIALOG.REMOVE_EVENT_NAME_TITLE`}
                                                confirmTextKey={`${TAB_PREFIX}.CONFIRM_DIALOG.REMOVE_EVENT_NAME_TEXT`}
                                                shouldConfirm={!!trimmedKey(name)}
                                                onConfirm={() => removeEventName(i)}
                                            />
                                        )}
                                    </div>
                                ))}
                                {!readOnly && (
                                    <Button
                                        type="link"
                                        Icon="plus"
                                        iconVariant={IconVariants.ACTIVE}
                                        onClick={addEventName}
                                        className={css.addButton}
                                    >
                                        <Translate id={`${TAB_PREFIX}.ADD_EVENT_NAME`} />
                                    </Button>
                                )}
                            </div>
                        </FormField>
                    </>
                )}
            </FormInnerSection>
            {dialog}
        </>
    );
};

EventMappingSection.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(EventMappingSection);
